import { ReactElement } from 'react';
import { FieldValues } from 'react-hook-form';

import {
  INumberTextFieldProps,
  NumberTextField,
} from 'modules/common/components/NumberTextField';
import { TokenIcon } from 'modules/common/components/TokenIcon';
import { Tooltip } from 'modules/common/components/Tooltip';
import { ArrowIcon } from 'modules/common/icons';
import { useTranslation } from 'modules/i18n';

import { translation } from './translation';
import { useStyles } from './useStyles';

interface INumberInputsProps<T extends FieldValues = FieldValues>
  extends Omit<INumberTextFieldProps<T>, 'ref'> {
  inputToken: 'MAT' | 'stMAT';
  inputTooltip?: string;
  receiveTooltip?: string;
  receiveToken: 'MAT' | 'stMAT';
  receiveControlName: string;
}

export function NumberInputs({
  inputToken,
  inputTooltip,
  receiveToken,
  receiveTooltip,
  control,
  receiveControlName,
  variant,
  error,
  helperText,
  ...props
}: INumberInputsProps): ReactElement {
  const { t, keys } = useTranslation(translation);
  const { classes, cx } = useStyles();

  return (
    <div className={classes.root}>
      <NumberTextField
        control={control}
        error={error}
        label={
          <div className={classes.label}>
            <span>{t(keys.inputLabel, { token: inputToken })}</span>

            {inputTooltip && <Tooltip title={inputTooltip} />}
          </div>
        }
        unit={inputToken}
        variant={variant}
        {...props}
      />

      <div className={classes.inputContainer}>
        <NumberTextField
          autoFocus={false}
          control={control}
          FormHelperTextProps={{
            classes: { root: error ? classes.errorText : undefined },
          }}
          helperText={helperText}
          InputProps={{
            readOnly: true,
            tabIndex: -1,
            classes: {
              input: cx(
                classes.receiveInput,
                error ? classes.errorText : undefined,
              ),
              root: classes.receiveRoot,
            },
            startAdornment: (
              <TokenIcon className={classes.tokenIcon} token={receiveToken} />
            ),
          }}
          label={
            <div className={classes.label}>
              <span>{t(keys.receiveLabel, { token: receiveToken })}</span>

              {receiveTooltip && <Tooltip title={receiveTooltip} />}
            </div>
          }
          name={receiveControlName}
          shouldValidate={false}
          unit={receiveToken}
          variant={variant}
        />

        <div className={classes.button}>
          <ArrowIcon className={classes.arrow} />
        </div>
      </div>
    </div>
  );
}
